import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import i18n from '@app/i18n'

import { CartApi } from '@features/cart/api'
import { useModalStore } from '@features/modal'
import InfoModal from '@features/modal/ui/error'
import styles from '@features/modal/ui/styles.module.scss'

import { client } from '@shared/api/client'
import {
  CartDataInput,
  CartInfoQuery,
  CartInfoQueryVariables,
  StockProductInput,
  StockProductResult,
} from '@shared/generated'

const cartApi = new CartApi(client)

const modalStore = useModalStore.getState()

const t = i18n.t

const openErrorInfoModal = (errorTransKey?: string) => {
  modalStore.openModal('info', <InfoModal />, {
    title: t('common:modal.error.title'),
    description:
      t(`errors:${errorTransKey}`) ?? t('common:modal.error.description'),
    classes: {
      content: styles['error-info-modal-content'],
    },
  })
}

export interface CartProduct {
  product_id: string;
  amount: number;
  comment?: string;
}

interface CartData {
  items: CartProduct[];
  promocode?: string;
}

interface ServerCart {
  shop_id: string;
  items: CartProduct[];
  promocode?: string;
}

interface MergeCartsParams {
  serverCarts: ServerCart[];
  localCarts: Record<string, CartData>;
}

function mergeCarts ({
  serverCarts,
  localCarts,
}: MergeCartsParams): Record<string, CartData> {
  const mergedCarts: Record<string, CartData> = {}

  serverCarts.forEach((serverCart) => {
    const shopId = serverCart.shop_id
    const localCart = localCarts[shopId]
    const mergedItemsMap = new Map<string, CartProduct>()

    serverCart.items.forEach((item) => {
      mergedItemsMap.set(item.product_id, { ...item })
    })

    if (localCart) {
      localCart.items.forEach((localItem) => {
        const existing = mergedItemsMap.get(localItem.product_id)
        if (existing) {
          mergedItemsMap.set(localItem.product_id, {
            ...existing,
            amount: Math.max(existing.amount, localItem.amount),
            comment: localItem.comment || existing.comment,
          })
        }
        else {
          mergedItemsMap.set(localItem.product_id, { ...localItem })
        }
      })
    }

    mergedCarts[shopId] = {
      items: Array.from(mergedItemsMap.values()),
      promocode: localCart?.promocode || serverCart.promocode,
    }
  })

  Object.keys(localCarts).forEach((shopId) => {
    if (!mergedCarts[shopId]) {
      mergedCarts[shopId] = localCarts[shopId]
    }
  })

  return mergedCarts
}

interface CartState {
  carts: Record<string, CartData>;
  currentShopId?: string;
  isSyncing: boolean;
  cartInfoData: CartInfoQuery['cartInfo'];
  cartInfoLoading: boolean;

  commentedPositions: Record<string, string[]>;

  setShop: (shopId: string) => void;
  addOrUpdateProduct: (product: CartProduct) => void;
  removeProduct: (productId: string) => void;
  clearCart: () => void;
  setPromocode: (promocode: string) => void;
  getTotalQuantity: () => number;
  getCart: () =>
    | { shop_id: string; items: CartProduct[]; promocode?: string }
    | undefined;
  isProductInCart: (productId: string) => boolean;
  getProduct: (productId: string) => CartProduct | undefined;
  updateProductComment: (productId: string, comment: string) => void;
  hasComment: (productId: string) => boolean;
  removeProductComment: (productId: string) => void;
  loadCarts: () => Promise<void>;
  syncCart: (currentCart?: CartData) => Promise<void>;
  checkProductsInStock: () => Promise<boolean | undefined>;
  removeZeroAmountItemsInCurrentCart: (isAuthorized: boolean) => Promise<void>;
  setCurrentCart: (cart: CartData) => void;
  getCartInfoData: (infoDataVariables: CartInfoQueryVariables) => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      carts: {},
      currentShopId: undefined,
      isSyncing: false,
      cartInfoData: null,
      cartInfoLoading: false,

      commentedPositions: {},

      setShop: (shopId) => {
        set((state) => ({
          currentShopId: shopId,
          carts: {
            ...state.carts,
            [shopId]: state.carts[shopId] ?? {
              items: [],
              promocode: undefined,
            },
          },
        }))
      },

      addOrUpdateProduct: (product) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId] ?? {
            items: [],
            promocode: undefined,
          }
          const idx = cart.items.findIndex(
            (p) => p.product_id === product.product_id,
          )
          const items = [ ...cart.items ]

          if (idx !== -1) items[idx] = { ...items[idx], ...product }
          else items.push(product)

          return {
            carts: { ...state.carts, [shopId]: { ...cart, items } },
          }
        })
      },

      removeProduct: (productId) => {
        const shopId = get().currentShopId

        if (!shopId) {
          return
        }

        set((state) => {
          const cart = state.carts[shopId]
          if (!cart) {
            return {}
          }

          return {
            commentedPositions: {
              ...state.commentedPositions,
              [shopId]: (state.commentedPositions[shopId] ?? []).filter((id) => id !== productId),
            },
            carts: {
              ...state.carts,
              [shopId]: {
                ...cart,
                items: cart.items.filter((p) => p.product_id !== productId),
              },
            },
          }
        })
      },

      clearCart: () => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => ({
          commentedPositions: {
            ...state.commentedPositions,
            [shopId]: [],
          },
          carts: {
            ...state.carts,
            [shopId]: { items: [], promocode: undefined },
          },
        }))
      },

      setPromocode: (promocode) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId] ?? {
            items: [],
            promocode: undefined,
          }
          return {
            carts: { ...state.carts, [shopId]: { ...cart, promocode } },
          }
        })
      },

      getCart: () => {
        const shopId = get().currentShopId
        if (!shopId) return undefined
        const cart = get().carts[shopId]
        return cart
          ? { shop_id: shopId, items: cart.items, promocode: cart.promocode }
          : undefined
      },

      isProductInCart: (productId) => {
        const shopId = get().currentShopId
        return (
          !!shopId &&
          !!get().carts[shopId]?.items.some((p) => p.product_id === productId)
        )
      },

      getProduct: (productId) => {
        const shopId = get().currentShopId
        return shopId
          ? get().carts[shopId]?.items.find((p) => p.product_id === productId)
          : undefined
      },

      getTotalQuantity: () => {
        const shopId = get().currentShopId
        return shopId ? (get().cartInfoData?.products?.length as number ?? 0) : 0
      },

      updateProductComment: (productId, comment) => {
        const shopId = get().currentShopId

        if (!shopId) {
          return
        }

        set((state) => {
          const cart = state.carts[shopId]

          if (!cart) {
            return {}
          }

          const nextState = {
            commentedPositions: {
              ...state.commentedPositions,
              [shopId]: (state.commentedPositions[shopId] ?? []).concat(productId),
            },
            carts: {
              ...state.carts,
              [shopId]: {
                ...cart,
                items: cart.items.map((item) =>
                  item.product_id === productId ? { ...item, comment } : item,
                ),
              },
            },
          }

          return nextState
        })

        // Sync with backend
        get().syncCart()
      },

      /**
       * @deprecated use commentedPositionsSelector instead
       */
      hasComment: (productId) => {
        const state = get()
        return commentedPositionsSelector(state)?.includes(productId) ?? false
      },

      removeProductComment: (productId) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId]
          if (!cart) return {}
          return {
            commentedPositions: {
              ...state.commentedPositions,
              [shopId]: (state.commentedPositions[shopId] ?? []).filter((id) => id !== productId),
            },
            carts: {
              ...state.carts,
              [shopId]: {
                ...cart,
                items: cart.items.map((item) =>
                  item.product_id === productId
                    ? { ...item, comment: undefined }
                    : item,
                ),
              },
            },
          }
        })

        // Sync with backend
        get().syncCart()
      },

      loadCarts: async () => {
        try {
          set({
            isSyncing: true,
          })

          const serverCarts = await cartApi.loadCarts()

          const carts = mergeCarts({
            serverCarts: serverCarts.data.loadCarts,
            localCarts: get().carts,
          })

          set(() => ({
            carts,
          }))

          const syncCarts = Object.entries(carts).map(([ name, value ]) => {
            return {
              shop_id: name,
              ...value,
            }
          })

          await cartApi.initialSyncCarts(syncCarts)

          await get().checkProductsInStock()
        }
        catch (error) {
          console.error('Ошибка загрузки корзин', error)
          openErrorInfoModal('load_carts_error')
        }
        finally {
          set({
            isSyncing: false,
          })
        }
      },

      syncCart: async (cart) => {
        try {
          set({ isSyncing: true })

          const shopId = get().currentShopId
          const currentCart = cart ?? get().getCart()

          if (!shopId || !currentCart) return

          const response = await cartApi.syncCart({
            shop_id: shopId,
            items: currentCart.items as CartDataInput[],
          })

          const updatedItems = response.data?.syncCart

          if (updatedItems?.length) {
            set((state) => {
              const currentItems = state.carts[shopId]?.items ?? []
              // Preserve comments from local state when syncing
              const commentsMap = new Map(
                currentItems
                  .filter((item) => item.comment)
                  .map((item) => [ item.product_id, item.comment ])
              )

              return {
                carts: {
                  ...state.carts,
                  [shopId]: {
                    ...state.carts[shopId],
                    items: updatedItems.map(
                      ({ product_id, amount }: CartProduct) => ({
                        product_id,
                        amount,
                        comment: commentsMap.get(product_id),
                      }),
                    ),
                  },
                },
              }
            })
          }
        }
        catch (error) {
          console.error('Ошибка синхронизации корзины', error)
          openErrorInfoModal('sync_cart')
        }
        finally {
          set({ isSyncing: false })
        }
      },

      checkProductsInStock: async () => {
        let hasChanges = false

        try {
          set({
            isSyncing: true,
          })

          const shopId = get().currentShopId
          const cart = get().getCart()

          if (!shopId || !cart?.items) return

          const cartData: StockProductInput = {
            shop_id: shopId,
            items: cart?.items.map((i) => {
              return {
                product_id: i.product_id,
                amount: i.amount,
              }
            }),
          }

          const data = await cartApi.checkProductsInStock(cartData)

          const checkProductsData = data.data
            ?.checkProductsInStock as StockProductResult[]

          if (checkProductsData) {
            const cart = get().getCart()

            for (const checkProduct of checkProductsData) {
              const findProductInCart = cart?.items.find(
                (i) => i.product_id === checkProduct.product_id,
              )

              if (!findProductInCart) continue

              if (findProductInCart.amount > checkProduct.max_amount) {
                get().addOrUpdateProduct({
                  product_id: checkProduct.product_id,
                  amount: checkProduct.max_amount,
                })

                hasChanges = true
              }
            }
          }
        }
        catch (error) {
          console.error('Ошибка проверки наличия товара', error)
          openErrorInfoModal('check_products_in_stock')
        }
        finally {
          set({
            isSyncing: false,
          })

          if (hasChanges) {
            openErrorInfoModal('changes_in_cart')
          }
        }

        return hasChanges
      },

      setCurrentCart: (cart) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => ({
          carts: {
            ...state.carts,
            [shopId]: cart,
          },
        }))
      },

      removeZeroAmountItemsInCurrentCart: async (isAuthorized: boolean) => {
        const shopId = get().currentShopId
        if (!shopId) return

        set((state) => {
          const cart = state.carts[shopId]
          if (!cart) return {}

          const filteredItems = cart.items.filter((item) => item.amount > 0)
          if (filteredItems.length === cart.items.length) return {}

          return {
            carts: {
              ...state.carts,
              [shopId]: {
                ...cart,
                items: filteredItems,
              },
            },
          }
        })

        if (isAuthorized) {
          await get().syncCart()
        }
      },

      getCartInfoData: async (infoDataVariables: CartInfoQueryVariables) => {
        try {
          set({ cartInfoLoading: true })
          const data = await cartApi.cartInfoData(infoDataVariables)
          const cartInfo = data?.data?.cartInfo || null

          set({ cartInfoData: cartInfo })
        }
        catch (error) {
          console.error('getCartInfoData error: ', error)
          openErrorInfoModal('get_cart_info_data')
        }
        finally {
          set({ cartInfoLoading: false })
        }
      },
    }),
    { name: 'cart-storage' },
  ),
)

/**
 * Stable empty array reference to prevent infinite re-renders
 */
const EMPTY_ARRAY: string[] = []

/**
 * Selector to get commented positions for the current shop
 * @returns Array of product ids that are commented
 */
export const commentedPositionsSelector = (state: CartState) => {
  const { currentShopId, commentedPositions = {} } = state
  if (!currentShopId || !commentedPositions[currentShopId]) {
    return EMPTY_ARRAY
  }
  return commentedPositions[currentShopId]
}
